import { Auth, cookieUtils } from '../utils';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';
const PLATFORM_URL = process.env.REACT_APP_PLATFORM_URL || '';

/**
 * Subsystem configuration for API routing.
 *  - 'playground': Routes to REACT_APP_SERVER_URL (playground server)
 *  - 'platform':   Routes to REACT_APP_PLATFORM_URL (platform-node)
 *
 * Both subsystems are authenticated with the same Authorization Bearer JWT,
 * read from the client-side `Authorization` cookie. Mirrors ai-canvas's
 * pattern — no BFF cookie handling.
 */
type Subsystem = 'playground' | 'platform';

export const PLAYGROUND: Subsystem = 'playground';
export const PLATFORM: Subsystem = 'platform';

interface RequestOptions extends RequestInit {
  /**
   * Required: Specify which subsystem to route to.
   */
  subsystem: Subsystem;
}

type UnauthorizedHandler = (status: number, code?: string, message?: string) => void;
type ForbiddenHandler = (status: number, code?: string, message?: string) => void;

/**
 * Centralized API client with authentication interceptors and subsystem routing
 *
 * Features:
 * - Automatically adds Authorization header with JWT token
 * - Handles 401 (invalid token) and 403 (forbidden) responses separately
 * - Routes requests to appropriate backend based on required subsystem parameter
 *
 * Subsystem Routing:
 * - 'playground' subsystem → REACT_APP_SERVER_URL (backend server with /api/* endpoints)
 * - 'platform' subsystem → REACT_APP_PLATFORM_URL (platform service with /user/*, /auth/*, /file/*)
 *
 * Usage Examples:
 *
 * ```typescript
 * import { apiClient, PLAYGROUND, PLATFORM } from '../services/apiClient';
 *
 * // All requests MUST specify subsystem using constants
 * apiClient.get('/api/users', PLAYGROUND);
 * apiClient.post('/user/update', PLATFORM, data);
 * apiClient.put('/api/sessions/123', PLAYGROUND, updates);
 * apiClient.delete('/file/abc', PLATFORM);
 * ```
 */
class ApiClient {
  private serverUrl: string;
  private platformUrl: string;
  private onUnauthorized: UnauthorizedHandler | null = null;
  private onForbidden: ForbiddenHandler | null = null;

  constructor(serverUrl: string, platformUrl: string) {
    this.serverUrl = serverUrl;
    this.platformUrl = platformUrl;
  }

  /**
   * Set handler for unauthorized (401) responses
   * Called when token is invalid or expired
   */
  setUnauthorizedHandler(handler: UnauthorizedHandler) {
    this.onUnauthorized = handler;
  }

  /**
   * Set handler for forbidden (403) responses
   * Called when email is not whitelisted
   */
  setForbiddenHandler(handler: ForbiddenHandler) {
    this.onForbidden = handler;
  }

  /**
   * Determine which base URL to use based on subsystem
   * @param subsystem - Required subsystem ('playground' or 'platform')
   */
  private getBaseUrl(subsystem: Subsystem): string {
    return subsystem === 'platform' ? this.platformUrl : this.serverUrl;
  }

  /**
   * Get authorization headers with JWT token from the Authorization cookie.
   * The cookie value may have been URL-encoded by cookieUtils.create; we try
   * to decode and fall back to the raw value if decoding throws.
   */
  private getAuthHeaders(): Record<string, string> {
    const token = cookieUtils.get(Auth.TOKEN);
    if (!token) return {};
    try {
      const decoded = decodeURIComponent(token);
      return { Authorization: `Bearer ${decoded}` };
    } catch {
      return { Authorization: `Bearer ${token}` };
    }
  }

  /**
   * Make an authenticated API request. Auth travels via the Authorization
   * Bearer header, read from the client-side cookie. No `credentials: 'include'`
   * needed — we don't rely on the browser sending cookies cross-origin.
   */
  async request<T>(endpoint: string, options: RequestOptions): Promise<T> {
    const { subsystem, headers = {}, ...rest } = options;

    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(headers as Record<string, string>),
      ...this.getAuthHeaders(),
    };

    const baseUrl = this.getBaseUrl(subsystem);

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...rest,
      headers: requestHeaders,
    });

    // Handle 401 (invalid/expired token) - trigger logout
    if (response.status === 401) {
      const error = await response.json().catch(() => ({ message: 'Unauthorized' }));

      if (this.onUnauthorized) {
        this.onUnauthorized(401, error.code, error.message);
      }

      throw new ApiError(error.message || 'Unauthorized', 401, error.code);
    }

    // Handle 403 (email not whitelisted) - redirect to access denied
    if (response.status === 403) {
      const error = await response.json().catch(() => ({ message: 'Forbidden' }));

      if (this.onForbidden) {
        this.onForbidden(403, error.code, error.message);
      }

      throw new ApiError(error.message || 'Access Denied', 403, error.code);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      throw new ApiError(error.message || 'Request failed', response.status);
    }

    return response.json();
  }

  /**
   * GET request
   * @param endpoint - API endpoint
   * @param subsystem - Required subsystem ('playground' or 'platform')
   * @param options - Optional additional request options
   */
  get<T>(endpoint: string, subsystem: Subsystem, options?: Omit<RequestOptions, 'subsystem'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, subsystem, method: 'GET' });
  }

  /**
   * POST request
   * @param endpoint - API endpoint
   * @param subsystem - Required subsystem ('playground' or 'platform')
   * @param body - Request body
   * @param options - Optional additional request options
   */
  post<T>(endpoint: string, subsystem: Subsystem, body?: unknown, options?: Omit<RequestOptions, 'subsystem'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      subsystem,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   * @param endpoint - API endpoint
   * @param subsystem - Required subsystem ('playground' or 'platform')
   * @param body - Request body
   * @param options - Optional additional request options
   */
  put<T>(endpoint: string, subsystem: Subsystem, body?: unknown, options?: Omit<RequestOptions, 'subsystem'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      subsystem,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   * @param endpoint - API endpoint
   * @param subsystem - Required subsystem ('playground' or 'platform')
   * @param options - Optional additional request options
   */
  delete<T>(endpoint: string, subsystem: Subsystem, options?: Omit<RequestOptions, 'subsystem'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, subsystem, method: 'DELETE' });
  }
}

/**
 * Custom API error with status code and error code
 */
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotAllowed(): boolean {
    return this.code === 'EMAIL_NOT_ALLOWED';
  }
}

// Create singleton instance
export const apiClient = new ApiClient(SERVER_URL, PLATFORM_URL);

export default apiClient;